import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, InputBase, Popover, Typography } from '@mui/material';
import { ExpandMore as ChevronIcon, Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { initialsOf, kindKeyOf, ScopedOrganization } from './featureFlagTypes';

interface OrganizationOption {
  id: string;
  name?: string;
  displayName?: string;
  type?: string;
}

/** The 24px initials tile beside an organization name. */
export const OrgTile: React.FC<{ name?: string; empty?: boolean }> = ({ name, empty = false }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      flex: '0 0 auto',
      display: 'grid',
      placeItems: 'center',
      width: 24,
      height: 24,
      borderRadius: '6px',
      bgcolor: empty ? tokens.surface.tint : tokens.surface.header,
      color: tokens.ink.inverse,
      fontSize: 10.5,
      fontWeight: 700,
    }}
  >
    {empty ? '' : initialsOf(name ?? '')}
  </Box>
);

interface OrganizationPickerProps {
  value: ScopedOrganization | null;
  onChange: (org: ScopedOrganization) => void;
  /** Overrides per organization id, for the "Store · 2 overrides" line. */
  overrideCounts: Map<string, number>;
}

/**
 * "Overrides for [organization]": a button that opens an async search over
 * `GET /admin/organizations?search=` (hundreds of organizations, so the server
 * does the matching). An empty search lists the first page.
 */
export const OrganizationPicker: React.FC<OrganizationPickerProps> = ({ value, onChange, overrideCounts }) => {
  const { t } = useAppTranslation();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timeout);
  }, [term]);

  const search = useQuery({
    queryKey: ['admin-feature-flags-org-search', debounced],
    queryFn: async (): Promise<OrganizationOption[]> => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ page: '1', limit: '20' });
      if (debounced) params.set('search', debounced);
      const res = await makeApiCall(`/admin/organizations?${params.toString()}`, { headers });
      if (!res.ok) return [];
      const body = await res.json();
      const raw = body?.data?.organizations ?? body?.data ?? [];
      return Array.isArray(raw) ? raw : [];
    },
    enabled: open,
    staleTime: 30_000,
  });

  const close = () => {
    setOpen(false);
    setTerm('');
  };

  const kindLabel = (type: string | null | undefined) => t(`admin.featureFlags.kinds.${kindKeyOf(type)}`);
  const options = search.data ?? [];

  return (
    <Box sx={{ flex: '0 1 340px', minWidth: 240 }}>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('admin.featureFlags.scope.pickerLabel')}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          height: 38,
          pl: 1,
          pr: 1.25,
          border: `1px solid ${open ? tokens.accent.main : tokens.line.default}`,
          borderRadius: '9px',
          bgcolor: tokens.surface.card,
          textAlign: 'left',
          '&:hover': { borderColor: open ? tokens.accent.main : tokens.ink.disabled },
        }}
      >
        <OrgTile name={value?.name} empty={!value} />
        <Typography
          component="span"
          noWrap
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            fontSize: 13,
            fontWeight: value ? 600 : 400,
            color: value ? tokens.ink.primary : tokens.ink.faint,
          }}
        >
          {value ? value.name : t('admin.featureFlags.scope.placeholder')}
        </Typography>
        {value && (
          <Typography component="span" sx={{ flex: '0 0 auto', fontSize: 11.5, color: tokens.ink.muted }}>
            {kindLabel(value.type)}
          </Typography>
        )}
        <ChevronIcon sx={{ flex: '0 0 auto', fontSize: 18, color: tokens.ink.muted }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: anchorRef.current?.offsetWidth ?? 340,
              maxWidth: 'calc(100vw - 32px)',
              p: 0.75,
              borderRadius: '11px',
              border: `1px solid ${tokens.line.default}`,
              boxShadow: tokens.shadow.menu,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 34,
            px: 1.25,
            mb: 0.5,
            border: `1px solid ${tokens.line.default}`,
            borderRadius: '8px',
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: tokens.ink.faint }} />
          <InputBase
            autoFocus
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('admin.featureFlags.scope.searchPlaceholder')}
            inputProps={{ 'aria-label': t('admin.featureFlags.scope.searchLabel') }}
            sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
          />
          {search.isFetching && <CircularProgress size={14} />}
        </Box>
        <Box role="listbox" aria-label={t('admin.featureFlags.scope.searchLabel')} sx={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: 320, overflowY: 'auto' }}>
          {options.map((option) => {
            const name = option.displayName || option.name || option.id;
            const count = overrideCounts.get(option.id) ?? 0;
            const selected = option.id === value?.id;
            const kind = kindLabel(option.type);
            return (
              <ButtonBase
                key={option.id}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange({ id: option.id, name, type: option.type ?? null });
                  close();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1,
                  py: '7px',
                  borderRadius: '8px',
                  textAlign: 'left',
                  bgcolor: selected ? tokens.accent.tint : 'transparent',
                  '&:hover': { bgcolor: selected ? tokens.accent.tint : tokens.surface.tint },
                }}
              >
                <OrgTile name={name} />
                <Typography component="span" noWrap sx={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>
                  {name}
                </Typography>
                <Typography component="span" sx={{ flex: '0 0 auto', fontSize: 11.5, color: tokens.ink.muted }}>
                  {count > 0
                    ? t('admin.featureFlags.scope.optionMeta', { kind, count })
                    : t('admin.featureFlags.scope.optionMetaNone', { kind })}
                </Typography>
              </ButtonBase>
            );
          })}
          {!search.isFetching && search.isFetched && options.length === 0 && (
            <Typography sx={{ px: 1, py: 1.25, fontSize: 13, color: tokens.ink.muted }}>
              {debounced
                ? t('admin.featureFlags.scope.noMatch', { q: debounced })
                : t('admin.featureFlags.scope.noOrganizations')}
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
};
